import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { coverSrc } from '@/lib/coverCredits'

// Real client case studies (each has its own /blog/*-case-study page). Newest /
// strongest first. Keep in sync with the blog index in pages/blog/BlogPage.tsx.
const caseStudies = [
  {
    slug: 'holy-trinity-case-study',
    title: 'Moving a Church Off WordPress Without Touching Their Email',
    description:
      'A bespoke rebuild for Holy Trinity Covina: off WordPress onto a fast static site scoring 96/100 on The Full Score, with the parish email untouched through the cutover.',
    highlights: ['96/100 Full Score', 'Email untouched at cutover', 'Off WordPress to static'],
  },
  {
    slug: 'sierra-canyon-case-study',
    title: 'A Digital Photo Competition for Sierra Canyon School',
    description:
      'Replacing a paper-based photo competition with a mobile-first digital platform for submissions and judging.',
    highlights: ['Mobile-first submissions', 'Digital judging', 'Paperless workflow'],
  },
  {
    slug: 'chroma-festival-case-study',
    title: "Building Chroma Festival's Complete Digital Infrastructure from Scratch",
    description:
      'A modern festival website, streamlined application system, and custom admin panel built in 8 weeks for a successful first season launch.',
    highlights: ['8-week timeline', 'Custom admin panel', '10+ hrs/week saved'],
  },
]

export default function ResourcesPage() {
  useEffect(() => {
    document.title = 'Resources for Music Leaders | Fugue Lab'
  }, [])

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const caseStudiesRef = useScrollReveal<HTMLDivElement>('.case-study-card', { stagger: 0.1 })
  const articlesRef = useStaggeredGrid<HTMLDivElement>('.article-card', { stagger: 0.1 })
  const downloadsRef = useStaggeredGrid<HTMLDivElement>('.download-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-16 px-4 py-16">
      {/* Header */}
      <section ref={heroRef} className="mx-auto max-w-3xl space-y-4 text-center">
        <h1 className="text-4xl font-bold md:text-5xl">Resources for Music Leaders</h1>
        <p className="text-muted-foreground text-lg">
          Free guides, case studies, and insights to help you modernize your organization&apos;s digital presence,
          streamline ticketing and applications, and grow audiences.
        </p>
      </section>

      {/* Case Studies */}
      <section className="space-y-8">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold">Case Studies</h2>
          <p className="text-muted-foreground">
            See how we&apos;ve helped classical music organizations build modern digital infrastructure and grow
            audiences.
          </p>
        </div>

        <div ref={caseStudiesRef} className="grid gap-6 md:grid-cols-1">
          {caseStudies.map((cs) => (
            <m.div
              key={cs.slug}
              className="case-study-card"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <Link
                to={`/blog/${cs.slug}`}
                className="group border-border hover:border-primary flex flex-col overflow-hidden rounded-lg border transition-colors md:flex-row"
              >
                <img
                  src={coverSrc(cs.slug)}
                  alt=""
                  width={1600}
                  height={1000}
                  loading="lazy"
                  className="h-48 w-full object-cover transition-transform duration-300 group-hover:scale-[1.02] md:h-auto md:w-2/5"
                />
                <div className="space-y-3 p-6 md:w-3/5">
                  <div className="text-primary text-sm font-semibold tracking-wide uppercase">Case Study</div>
                  <h3 className="text-2xl font-bold">{cs.title}</h3>
                  <p className="text-muted-foreground">{cs.description}</p>
                  <div className="text-muted-foreground flex flex-wrap gap-4 text-sm">
                    {cs.highlights.map((h) => (
                      <span key={h}>&bull; {h}</span>
                    ))}
                  </div>
                  <div className="text-primary font-semibold">Read the case study &rarr;</div>
                </div>
              </Link>
            </m.div>
          ))}
        </div>
      </section>

      {/* Blog Posts */}
      <section className="space-y-8">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold">Latest Articles</h2>
          <p className="text-muted-foreground">
            Practical insights on music organization websites, ticketing systems, and digital strategy.
          </p>
        </div>

        <div ref={articlesRef} className="grid gap-6 md:grid-cols-2">
          <m.div
            className="article-card"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <Link
              to="/blog/festivals-losing-applicants"
              className="group border-border hover:border-primary block h-full overflow-hidden rounded-lg border transition-colors"
            >
              <img
                src={coverSrc('festivals-losing-applicants')}
                alt=""
                width={1600}
                height={1000}
                loading="lazy"
                className="aspect-[16/10] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="space-y-3 p-6">
                <h3 className="text-xl font-bold">5 Ways Classical Music Festivals Lose Applicants Online</h3>
                <p className="text-muted-foreground text-sm">
                  Common website and funnel mistakes that cost festivals qualified applicants, and how to fix them.
                </p>
                <div className="text-primary text-sm font-semibold">Read article &rarr;</div>
              </div>
            </Link>
          </m.div>

          <m.div
            className="article-card"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <Link
              to="/blog/chroma-festival-case-study"
              className="group border-border hover:border-primary block h-full overflow-hidden rounded-lg border transition-colors"
            >
              <img
                src={coverSrc('chroma-festival-case-study')}
                alt=""
                width={1600}
                height={1000}
                loading="lazy"
                className="aspect-[16/10] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="space-y-3 p-6">
                <div className="text-primary text-xs font-semibold tracking-wide uppercase">Case Study</div>
                <h3 className="text-xl font-bold">Building Chroma Festival&apos;s Digital Infrastructure</h3>
                <p className="text-muted-foreground text-sm">
                  Website, application system, and admin panel built from scratch in 8 weeks.
                </p>
                <div className="text-primary text-sm font-semibold">Read case study &rarr;</div>
              </div>
            </Link>
          </m.div>
        </div>

        <div className="pt-4 text-center">
          <Link to="/blog">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="inline-block"
            >
              <Button variant="outline" className="cursor-pointer">
                View All Articles
              </Button>
            </m.div>
          </Link>
        </div>
      </section>

      {/* Guides */}
      <section className="space-y-8">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold">Guides</h2>
          <p className="text-muted-foreground">
            Reference material for the decisions that come up when you modernize an organization.
          </p>
        </div>

        <m.div whileHover={{ y: -4 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
          <Link
            to="/resources/grants"
            className="group border-border hover:border-primary block overflow-hidden rounded-lg border p-6 transition-colors"
          >
            <div className="text-primary text-sm font-semibold tracking-wide uppercase">Guide</div>
            <h3 className="mt-2 text-2xl font-bold">Grants That Fund Design Work</h3>
            <p className="text-muted-foreground mt-2">
              Which grant programs pay for branding, marketing, and website work, what their guidelines actually allow,
              and the scoping rule that gets rebrand applications declined.
            </p>
            <div className="text-primary mt-3 font-semibold">Read the guide &rarr;</div>
          </Link>
        </m.div>

        {/* The hymnal is a SEPARATE Vite build on this same origin (plan D9), so this
            cannot be a <Link> — react-router would try to resolve /hymnal/* inside
            THIS app's router and land on its catch-all. A plain <a> hands the
            navigation to the browser, which is what makes the two builds behave as
            one site. */}
        <m.div whileHover={{ y: -4 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
          <a
            href="/hymnal/hymnal1982"
            className="group border-border hover:border-primary block overflow-hidden rounded-lg border p-6 transition-colors"
          >
            <div className="text-primary text-sm font-semibold tracking-wide uppercase">Reference</div>
            <h3 className="mt-2 text-2xl font-bold">The Hymnal 1982: a reference index</h3>
            <p className="text-muted-foreground mt-2">
              All 720 hymns with their tunes, metres, composers, authors and translators, cross-linked so you can go
              from a text to its tune to every other tune in the same metre. Free, no account.
            </p>
            <div className="text-primary mt-3 font-semibold">Browse the hymnal &rarr;</div>
          </a>
        </m.div>

        {/* Same separate-build rule as the hymnal above: a plain <a>, not a <Link>.
            THIS CARD IS THE PSALTER'S ONLY INBOUND LINK. Until it existed, the 151
            psalter pages were reachable from the sitemap alone — crawled slowly,
            weighted as orphans, and inheriting none of the internal link equity of
            a hymnal already ranking on page one for its own queries. An orphan
            corpus is not published; it is merely uploaded. */}
        <m.div whileHover={{ y: -4 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
          <a
            href="/hymnal/psalter"
            className="group border-border hover:border-primary block overflow-hidden rounded-lg border p-6 transition-colors"
          >
            <div className="text-primary text-sm font-semibold tracking-wide uppercase">Reference</div>
            <h3 className="mt-2 text-2xl font-bold">The Psalter: all 150 psalms</h3>
            <p className="text-muted-foreground mt-2">
              The complete Psalter of the 1979 Book of Common Prayer. Every psalm in full, divided at the asterisk, with
              a printable PDF for each. Public domain, free, no account.
            </p>
            <div className="text-primary mt-3 font-semibold">Browse the psalter &rarr;</div>
          </a>
        </m.div>
      </section>

      {/* Free Downloads (Coming Soon) */}
      <section className="space-y-8">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold">Free Downloads</h2>
          <p className="text-muted-foreground">
            Practical guides and checklists to help you improve your organization&apos;s digital presence.
          </p>
        </div>

        <div ref={downloadsRef} className="grid gap-6 md:grid-cols-3">
          <m.div
            className="download-card border-border space-y-3 rounded-lg border p-6 opacity-75"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-4xl">📋</div>
            <h3 className="text-xl font-bold">Festival Digital Audit Checklist</h3>
            <p className="text-muted-foreground text-sm">
              12-point checklist to audit your festival&apos;s website, application process, and enrollment funnel.
            </p>
            <div className="text-muted-foreground text-sm italic">Coming soon</div>
          </m.div>

          <m.div
            className="download-card border-border space-y-3 rounded-lg border p-6 opacity-75"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-4xl">📄</div>
            <h3 className="text-xl font-bold">Festival Website RFP Template</h3>
            <p className="text-muted-foreground text-sm">
              Template for requesting proposals from web developers, including budget planning and vendor questions.
            </p>
            <div className="text-muted-foreground text-sm italic">Coming soon</div>
          </m.div>

          <m.div
            className="download-card border-border space-y-3 rounded-lg border p-6 opacity-75"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-4xl">🎯</div>
            <h3 className="text-xl font-bold">Application Funnel Setup Guide</h3>
            <p className="text-muted-foreground text-sm">
              Step-by-step guide to setting up enrollment tracking and optimizing your application process.
            </p>
            <div className="text-muted-foreground text-sm italic">Coming soon</div>
          </m.div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="bg-primary/5 border-primary/20 space-y-6 rounded-lg border p-12 text-center">
        <div className="cta-content">
          <h2 className="text-3xl font-bold">Need Help Implementing These Strategies?</h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl">
            Schedule a free 15-minute strategy call to discuss your organization&apos;s digital needs. We&apos;ll review
            your current setup and show you exactly how we can help.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Book a Free Call &rarr;
                </Button>
              </m.div>
            </a>
            <Link to="/pricing">
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  View Packages &amp; Pricing
                </Button>
              </m.div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
